import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import QuoteCommentsPanel from '../components/QuoteCommentsPanel';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Plus, FileText, CheckCircle2, Clock, Award, XCircle, Search, FileSpreadsheet, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BillingListPage() {
  const navigate = useNavigate();
  const { hasAnyRole } = useAuth();
  const [billings, setBillings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [actionData, setActionData] = useState({ remarks: '', invoice_no: '', invoice_amount: '', invoice_date: '' });
  const [processing, setProcessing] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [invoiceRejectDialogOpen, setInvoiceRejectDialogOpen] = useState(false);
  const [invoiceRejectRemarks, setInvoiceRejectRemarks] = useState('');
  
  // Check if user has read-only access (Project Head or Key Account Manager)
  const isReadOnly = hasAnyRole(['Project Head', 'Key Account Manager']) && !hasAnyRole(['Super Admin', 'Billing Manager', 'Billing Executive', 'Account Manager']);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter states for Status and Actions
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionsFilter, setActionsFilter] = useState('all');
  
  // Export states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchBillings();
  }, []);

  const fetchBillings = async () => {
    try {
      const response = await axios.get(`${API}/billing`);
      setBillings(response.data);
    } catch (error) {
      toast.error('Failed to load billings');
    } finally {
      setLoading(false);
    }
  };

  const handleAMValidation = async (billingId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/billing/${billingId}/am-validate`, {
        am_remarks: actionData.remarks,
        invoice_no: actionData.invoice_no,
        invoice_amount: parseFloat(actionData.invoice_amount),
        invoice_date: actionData.invoice_date
      });
      toast.success('Billing validated successfully');
      setSelectedBilling(null);
      setActionData({ remarks: '', invoice_no: '', invoice_amount: '', invoice_date: '' });
      fetchBillings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to validate');
    } finally {
      setProcessing(false);
    }
  };

  const handleAMReject = async (billingId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/billing/${billingId}/am-reject`, {
        rejection_remarks: rejectRemarks
      });
      toast.success('Billing rejected successfully');
      setRejectDialogOpen(false);
      setSelectedBilling(null);
      setRejectRemarks('');
      fetchBillings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkWorkDone = async (billingId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/billing/${billingId}/mark-work-done`, {
        remarks: actionData.remarks
      });
      toast.success('Work marked as done successfully');
      setSelectedBilling(null);
      fetchBillings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark work done');
    } finally {
      setProcessing(false);
    }
  };

  const handleInvoiceReject = async (billingId) => {
    if (!invoiceRejectRemarks.trim()) {
      toast.error('Rejection remarks are required');
      return;
    }
    setProcessing(true);
    try {
      await axios.post(`${API}/billing/${billingId}/invoice-reject`, {
        rejection_remarks: invoiceRejectRemarks
      });
      toast.success('Invoice rejected successfully');
      setInvoiceRejectDialogOpen(false);
      setSelectedBilling(null);
      setInvoiceRejectRemarks('');
      fetchBillings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject invoice');
    } finally {
      setProcessing(false);
    }
  };

  const handleFinalApproval = async (billingId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/billing/${billingId}/final-approve`, {
        final_remarks: actionData.remarks
      });
      toast.success('Billing finally approved successfully');
      setSelectedBilling(null);
      fetchBillings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  // Export to Excel handler
  const handleExportToExcel = async () => {
    if (!exportFromDate || !exportToDate) {
      toast.error('Please select both From and To dates');
      return;
    }
    
    if (new Date(exportFromDate) > new Date(exportToDate)) {
      toast.error('From date cannot be after To date');
      return;
    }
    
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/billing/export?from_date=${exportFromDate}&to_date=${exportToDate}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Billing_Management_${exportFromDate}_to_${exportToDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully');
      setExportDialogOpen(false);
      setExportFromDate('');
      setExportToDate('');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Determine what action is available for a billing
  const getActionType = (billing) => {
    if (isReadOnly) return 'view_only';
    if (hasAnyRole(['Account Manager', 'Super Admin']) && billing.status === 'pending') return 'validate';
    if (hasAnyRole(['Account Manager', 'Super Admin']) && billing.status === 'am_validated') return 'reject_invoice';
    if (hasAnyRole(['Billing Manager', 'Billing Executive', 'Super Admin']) && billing.status === 'am_validated') return 'mark_done';
    if (hasAnyRole(['Account Manager', 'Super Admin']) && billing.status === 'work_done') return 'final_approve';
    return 'none';
  };

  // Filter billings based on search query, status filter, and actions filter
  const filteredBillings = billings.filter(billing => {
    // Search filter
    const matchesSearch = !searchQuery || 
      billing.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      billing.quote_no?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || billing.status === statusFilter;
    
    // Actions filter
    const actionType = getActionType(billing);
    const matchesAction = actionsFilter === 'all' || 
      (actionsFilter === 'validate' && actionType === 'validate') ||
      (actionsFilter === 'mark_done' && actionType === 'mark_done') ||
      (actionsFilter === 'final_approve' && actionType === 'final_approve') ||
      (actionsFilter === 'reject_invoice' && actionType === 'reject_invoice') ||
      (actionsFilter === 'view_only' && actionType === 'view_only') ||
      (actionsFilter === 'none' && actionType === 'none');
    
    return matchesSearch && matchesStatus && matchesAction;
  });

  const getStatusBadge = (status) => {
    const variants = {
      pending: { className: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, text: 'PENDING' },
      am_validated: { className: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle2, text: 'AM VALIDATED' },
      rejected: { className: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, text: 'JMS REJECTED' },
      invoice_rejected: { className: 'bg-orange-100 text-orange-800 border-orange-200', icon: XCircle, text: 'INVOICE REJECTED' },
      work_done: { className: 'bg-purple-100 text-purple-800 border-purple-200', icon: FileText, text: 'WORK DONE' },
      final_approved: { className: 'bg-green-100 text-green-800 border-green-200', icon: Award, text: 'FINAL APPROVED' }
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Loading billings...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="space-y-6" data-testid="billing-list-page">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Billing Management
            </h1>
            <p className="text-muted-foreground mt-2">
              {filteredBillings.length} billing(s) found
              {searchQuery && ` (filtered from ${billings.length})`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setExportDialogOpen(true)}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
            {hasAnyRole(['Billing Manager', 'Billing Executive', 'Super Admin']) && (
              <Button onClick={() => navigate('/billing/create')} data-testid="create-billing-button">
                <Plus className="mr-2 h-4 w-4" />
                New Billing
              </Button>
            )}
          </div>
        </div>

        {/* Search Box and Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by JMS No or Quote No..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-billing-input"
            />
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="am_validated">AM Validated</SelectItem>
                <SelectItem value="work_done">Work Done</SelectItem>
                <SelectItem value="final_approved">Final Approved</SelectItem>
                <SelectItem value="rejected">JMS Rejected</SelectItem>
                <SelectItem value="invoice_rejected">Invoice Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Actions Filter */}
          <div className="flex items-center gap-2">
            <Select value={actionsFilter} onValueChange={setActionsFilter}>
              <SelectTrigger className="w-[180px]" data-testid="actions-filter">
                <SelectValue placeholder="Filter by Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="validate">Validate</SelectItem>
                <SelectItem value="mark_done">Mark Done</SelectItem>
                <SelectItem value="final_approve">Final Approve</SelectItem>
                <SelectItem value="reject_invoice">Reject Invoice</SelectItem>
                <SelectItem value="view_only">View Only</SelectItem>
                <SelectItem value="none">No Action</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card className="overflow-hidden glass-table">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="billing-table">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium">JMS No</th>
                  <th className="px-6 py-4 text-left text-sm font-medium">Quote No</th>
                  <th className="px-6 py-4 text-left text-sm font-medium">Order No</th>
                  <th className="px-6 py-4 text-left text-sm font-medium">Server Name</th>
                  <th className="px-6 py-4 text-left text-sm font-medium">Service Period</th>
                  <th className="px-6 py-4 text-left text-sm font-medium">Created By</th>
                  <th className="px-6 py-4 text-right text-sm font-medium">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-medium">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium">Date</th>
                  <th className="px-6 py-4 text-center text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredBillings.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-muted-foreground">
                      {searchQuery ? `No billings found for "${searchQuery}"` : 'No billings found'}
                    </td>
                  </tr>
                ) : (
                  filteredBillings.map((billing) => (
                    <tr key={billing.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm font-medium">{billing.invoice_number}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm">{billing.quote_no}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm">{billing.order_no || '-'}</span>
                      </td>
                      <td className="px-6 py-4 text-sm">{billing.server_name || '-'}</td>
                      <td className="px-6 py-4 text-sm">{billing.service_period || '-'}</td>
                      <td className="px-6 py-4 text-sm">{billing.created_by_name}</td>
                      <td className="px-6 py-4 text-sm text-right font-mono">
                        <span className="inline-flex items-center">
                          ₹{billing.total_amount?.toLocaleString('en-IN')}
                          <QuoteCommentsPanel quoteId={billing.quote_id} quoteNo={billing.quote_no} />
                        </span>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(billing.status)}</td>
                      <td className="px-6 py-4 text-sm">
                        {new Date(billing.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {isReadOnly ? (
                          <Badge variant="secondary" className="text-xs">View Only</Badge>
                        ) : (
                          <>
                        {hasAnyRole(['Account Manager', 'Super Admin']) && billing.status === 'pending' && (
                          <div className="flex gap-2 justify-center">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-green-500 text-green-600 hover:bg-green-50"
                                  onClick={() => {
                                    setSelectedBilling(billing);
                                    setActionData({ remarks: '', invoice_no: '', invoice_amount: '', invoice_date: '' });
                                  }}
                                >
                                  Validate
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>AM Validation</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                                    <Label>JMS No: {billing.invoice_number}</Label>
                                    <p className="text-sm text-muted-foreground">
                                      Amount: ₹{billing.total_amount?.toLocaleString('en-IN')}
                                    </p>
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="invoice_no">Invoice No *</Label>
                                    <Input
                                      id="invoice_no"
                                      value={actionData.invoice_no}
                                      onChange={(e) => setActionData({ ...actionData, invoice_no: e.target.value })}
                                      placeholder="Enter invoice number"
                                      required
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="invoice_amount">Invoice Amount (₹) *</Label>
                                    <Input
                                      id="invoice_amount"
                                      type="number"
                                      step="0.01"
                                      value={actionData.invoice_amount}
                                      onChange={(e) => setActionData({ ...actionData, invoice_amount: e.target.value })}
                                      placeholder="Enter invoice amount"
                                      required
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="invoice_date">Invoice Date *</Label>
                                    <Input
                                      id="invoice_date"
                                      type="date"
                                      value={actionData.invoice_date}
                                      onChange={(e) => setActionData({ ...actionData, invoice_date: e.target.value })}
                                      required
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="remarks">Validation Remarks</Label>
                                    <Textarea
                                      id="remarks"
                                      value={actionData.remarks}
                                      onChange={(e) => setActionData({ ...actionData, remarks: e.target.value })}
                                      rows={3}
                                    />
                                  </div>
                                  <Button
                                    onClick={() => handleAMValidation(billing.id)}
                                    disabled={processing || !actionData.invoice_no || !actionData.invoice_amount || !actionData.invoice_date}
                                    className="w-full bg-green-600 hover:bg-green-700"
                                  >
                                    {processing ? 'Validating...' : 'Confirm Validation'}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                            
                            <Dialog open={rejectDialogOpen && selectedBilling?.id === billing.id} onOpenChange={(open) => {
                              setRejectDialogOpen(open);
                              if (!open) {
                                setRejectRemarks('');
                              }
                            }}>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    setSelectedBilling(billing);
                                    setRejectDialogOpen(true);
                                  }}
                                >
                                  Reject
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle className="text-red-600">Reject Billing</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                                    <Label>JMS No: {billing.invoice_number}</Label>
                                    <p className="text-sm text-muted-foreground">
                                      Amount: ₹{billing.total_amount?.toLocaleString('en-IN')}
                                    </p>
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="reject_remarks">Rejection Remarks *</Label>
                                    <Textarea
                                      id="reject_remarks"
                                      value={rejectRemarks}
                                      onChange={(e) => setRejectRemarks(e.target.value)}
                                      rows={3}
                                      placeholder="Enter reason for rejection"
                                      required
                                    />
                                  </div>
                                  <Button
                                    onClick={() => handleAMReject(billing.id)}
                                    disabled={processing || !rejectRemarks.trim()}
                                    className="w-full bg-red-600 hover:bg-red-700"
                                  >
                                    {processing ? 'Rejecting...' : 'Confirm Rejection'}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        )}

                        {hasAnyRole(['Account Manager', 'Super Admin']) && billing.status === 'am_validated' && (
                          <div className="flex gap-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedBilling(billing);
                                    setActionData({ remarks: '' });
                                  }}
                                >
                                  Mark Done
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Mark Work Done</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                                    <Label>Invoice: {billing.invoice_number}</Label>
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="remarks">Remarks</Label>
                                    <Textarea
                                      id="remarks"
                                      value={actionData.remarks}
                                      onChange={(e) => setActionData({ remarks: e.target.value })}
                                      rows={3}
                                    />
                                  </div>
                                  <Button
                                    onClick={() => handleMarkWorkDone(billing.id)}
                                    disabled={processing}
                                    className="w-full"
                                  >
                                    {processing ? 'Processing...' : 'Mark as Done'}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                            
                            {hasAnyRole(['Account Manager']) && (
                              <Dialog open={invoiceRejectDialogOpen && selectedBilling?.id === billing.id} onOpenChange={(open) => {
                                setInvoiceRejectDialogOpen(open);
                                if (!open) {
                                  setInvoiceRejectRemarks('');
                                }
                              }}>
                                <DialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                    onClick={() => {
                                      setSelectedBilling(billing);
                                      setInvoiceRejectDialogOpen(true);
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Reject Invoice</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                      <Label>JMS No: {billing.invoice_number}</Label>
                                      <p className="text-sm text-muted-foreground">Quote: {billing.quote_no}</p>
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="invoice_reject_remarks">Rejection Remarks *</Label>
                                      <Textarea
                                        id="invoice_reject_remarks"
                                        value={invoiceRejectRemarks}
                                        onChange={(e) => setInvoiceRejectRemarks(e.target.value)}
                                        rows={3}
                                        placeholder="Enter reason for rejection"
                                        required
                                      />
                                    </div>
                                    <Button
                                      onClick={() => handleInvoiceReject(billing.id)}
                                      disabled={processing || !invoiceRejectRemarks.trim()}
                                      className="w-full bg-red-600 hover:bg-red-700"
                                    >
                                      {processing ? 'Rejecting...' : 'Confirm Rejection'}
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                          </div>
                        )}

                        {hasAnyRole(['Billing Manager', 'Super Admin']) && billing.status === 'work_done' && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedBilling(billing);
                                  setActionData({ remarks: '' });
                                }}
                              >
                                Final Approve
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Final Approval</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                  <Label>Invoice: {billing.invoice_number}</Label>
                                  <p className="text-sm text-muted-foreground">
                                    Amount: ₹{billing.total_amount?.toLocaleString('en-IN')}
                                  </p>
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="remarks">Final Remarks</Label>
                                  <Textarea
                                    id="remarks"
                                    value={actionData.remarks}
                                    onChange={(e) => setActionData({ remarks: e.target.value })}
                                    rows={3}
                                    required
                                  />
                                </div>
                                <Button
                                  onClick={() => handleFinalApproval(billing.id)}
                                  disabled={processing || !actionData.remarks}
                                  className="w-full"
                                >
                                  {processing ? 'Approving...' : 'Final Approve'}
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}

                        {billing.status === 'final_approved' && (
                          <Badge variant="secondary" className="text-xs">Completed</Badge>
                        )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Export to Excel Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle>Export to Excel</DialogTitle>
            <DialogDescription>
              Select a date range to export billing data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="export_from_date">From Date *</Label>
              <Input
                id="export_from_date"
                type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                data-testid="export-from-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="export_to_date">To Date *</Label>
              <Input
                id="export_to_date"
                type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                data-testid="export-to-date"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleExportToExcel}
                disabled={exporting || !exportFromDate || !exportToDate}
                className="flex-1"
                data-testid="confirm-export-btn"
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setExportDialogOpen(false);
                  setExportFromDate('');
                  setExportToDate('');
                }}
                disabled={exporting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
